/**
 * CashPlanSections — Phase 86.I Pass B (core).
 *
 * Renders GET /api/cash-plan and nothing else. Every figure that the model
 * couldn't compute shows its reason and next step rather than a zero, recorded
 * and converted amounts stay labelled, and the allocation breakdown is only
 * drawn when the model says it reconciles.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, AlertTriangle, Wallet, ShieldCheck, Target, TrendingUp, TrendingDown, Minus, CalendarClock, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { formatMoney } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { SectionHeading } from '@/components/liabilities/DebtPlanSections';
import { useAuth } from '@/context/AuthContext';
import { readCache, writeCache } from '@/lib/pageCache';
import {
  CardSkeleton, HeroSkeleton, ListSkeleton, SectionError, StripSkeleton,
  UpdatingChip, useLoadingPhase,
} from '@/components/common/SectionLoading';
import AssignJobsDialog from './AssignJobsDialog';
import BufferTargetControl from './BufferTargetControl';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = 'cash-plan';
const Ctx = createContext(null);

export function CashPlanProvider({ children, onUpdateBalance }) {
  const { user } = useAuth();
  const uid = user?.id;
  // Hydrate synchronously from the last completed calculation so a repeat visit
  // paints real numbers on the first frame instead of a skeleton.
  const [plan, setPlan] = useState(() => readCache(uid, CACHE_KEY));
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let dead = false;
    setBusy(true);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        // A refetch triggered by a local change must not be served the
        // pre-write answer from the server's short-lived memo.
        const res = await axios.get(`${API}/api/cash-plan${nonce > 0 ? '?fresh=1' : ''}`, {
          headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
          timeout: 45000,
        });
        if (dead) return;
        setPlan(res.data);
        setError(false);
        writeCache(uid, CACHE_KEY, res.data);
      } catch (e) {
        console.warn('cash-plan fetch failed', e?.response?.status || e?.message);
        if (!dead) setError(true);
      } finally {
        if (!dead) setBusy(false);
      }
    })();
    const onRefresh = () => setNonce((v) => v + 1);
    window.addEventListener('accounts:refresh', onRefresh);
    window.addEventListener('accounts:changed', onRefresh);
    return () => {
      dead = true;
      window.removeEventListener('accounts:refresh', onRefresh);
      window.removeEventListener('accounts:changed', onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, nonce]);

  const save = (next) => {
    setPlan(next);
    writeCache(uid, CACHE_KEY, next);
  };

  const value = {
    plan, busy, error, onUpdateBalance,
    // Usable content on screen while a fresh calculation is in flight.
    refreshing: busy && !!plan,
    setPlan: save,
    retry: () => { setError(false); setNonce((v) => v + 1); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCashPlan = () => useContext(Ctx)
  || { plan: null, busy: false, error: false, refreshing: false, setPlan: () => {}, retry: () => {} };

/** The accounts whose reported balance is provably behind their own activity. */
export const staleAccounts = (plan) => (plan?.accounts || []).filter(
  (a) => a.freshness && a.freshness.trusted === false);

/**
 * One button, three places. Whatever is withholding a figure, the fix is the
 * same: confirm the balance on the account that's behind.
 */
export function UpdateBalanceNudge({ tone = 'light', testid = 'update-balance-nudge' }) {
  const { plan, onUpdateBalance } = useCashPlan();
  const stale = staleAccounts(plan);
  if (!stale.length || !onUpdateBalance) return null;
  const one = stale.length === 1 ? stale[0] : null;
  const styles = tone === 'hero'
    ? 'border-white/25 text-white/90 hover:bg-white/10'
    : 'border-gold/40 bg-gold/10 text-[#7A4E08] hover:bg-gold/20';
  return (
    <button
      type="button"
      onClick={() => onUpdateBalance(one ? one.id : stale[0].id)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${styles}`}
      data-testid={testid}
    >
      <RefreshCw className="h-3 w-3" aria-hidden="true" />
      {one ? `Update ${one.name} balance` : `Update ${stale.length} balances`}
    </button>
  );
}

const money = (amount, ccy) => (amount == null ? null : formatMoney(amount, ccy, { compact: true }));

function Cell({ label, value, note, testid }) {
  return (
    <div className="px-4 py-3.5 sm:px-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{label}</p>
      <p className="font-display text-xl text-charcoal mt-1 leading-tight">{value}</p>
      {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
    </div>
  );
}

export function CashSummaryStrip() {
  const { plan, busy, error, refreshing, retry } = useCashPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    if (error) {
      return (
        <SectionError testid="cash-plan-error" onRetry={retry}
                      title="We couldn't work out your cash summary just now." />
      );
    }
    return <StripSkeleton phase={phase} message="Adding up your accounts&hellip;" />;
  }
  if (plan.state === 'no_cash') return null;
  const s = plan.summary;
  const ccy = plan.base_currency;
  const b = s.safety_buffer;

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border bg-card rounded-2xl border border-border shadow-sm mb-3"
           data-testid="cash-summary-strip" aria-busy={refreshing}>
        <Cell testid="cash-kpi-total" label="Total reported cash"
              value={money(s.total_reported.display_amount, ccy) || 'Not calculated yet'}
              note={`${s.total_reported.accounts_included} account${s.total_reported.accounts_included === 1 ? '' : 's'}${s.total_reported.converted ? ` · converted to ${ccy}` : ''}`} />
        <Cell testid="cash-kpi-bills" label="Available for bills"
              value={s.available_for_bills.state === 'ok' ? money(s.available_for_bills.display_amount, ccy) : 'Not assigned yet'}
              note={s.available_for_bills.state === 'ok' ? 'Assigned to everyday spending and bills' : 'Give your accounts a job'} />
        <Cell testid="cash-kpi-priorities" label="Assigned to priorities"
              value={s.assigned_to_priorities.state === 'ok' ? money(s.assigned_to_priorities.display_amount, ccy) : 'Not assigned yet'}
              note="Safety reserve and linked goals" />
        <Cell testid="cash-kpi-buffer" label="Safety buffer"
              value={b.state === 'ok' ? `${b.months} months` : 'Not calculated yet'}
              note={b.state === 'ok'
                ? `Target: ${b.target_months} months`
                : (b.unavailable_reason === 'no_safety_allocation_yet'
                  ? 'Assign cash to a safety reserve'
                  : 'Confirm your essential monthly spending')} />
      </div>
      {plan.explain?.conversion && (
        <p className="text-xs text-muted-foreground mb-5 -mt-1" data-testid="cash-fx-note">
          Held in {Object.keys(s.total_reported.by_currency || {}).sort().join(' and ')}.
          Totals are reported in {plan.explain.conversion.reporting_currency} at{' '}
          {Object.entries(plan.explain.conversion.rates || {})
            .filter(([, r]) => r)
            .map(([c, r]) => `1 ${c} = ${Number(r).toFixed(4)} ${plan.explain.conversion.reporting_currency}`)
            .join(', ') || 'today\u2019s rate'}{' '}
          ({plan.explain.conversion.rate_source}, {plan.explain.conversion.rate_date}).
          Each account below keeps its own currency.
        </p>
      )}
      {plan.freshness?.stale && (
        <div className="rounded-2xl border border-gold/30 bg-gold/5 px-4 py-3 mb-5 flex items-start gap-2"
             data-testid="cash-freshness-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-gold shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm text-charcoal">
              {plan.freshness.message}{' '}
              <span className="text-muted-foreground">
                Confirming it is what unblocks the 30-day view below.
              </span>
            </p>
            <div className="mt-2"><UpdateBalanceNudge tone="warning" testid="freshness-update-nudge" /></div>
          </div>
        </div>
      )}
    </>
  );
}

const PURPOSE_ROWS = [
  { key: 'everyday_bills', label: 'Everyday & bills', Icon: Wallet },
  { key: 'safety_reserve', label: 'Safety reserve', Icon: ShieldCheck },
  { key: 'goal_savings', label: 'Goal savings', Icon: Target },
  { key: 'not_assigned', label: 'Not yet assigned', Icon: Wallet },
];

function HowCalculated({ plan }) {
  const [open, setOpen] = useState(false);
  const e = plan.explain || {};
  return (
    <div className="mt-4 pt-3.5 border-t border-white/10">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="briefing-hero__secondary text-xs rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid="cash-how-calculated-toggle">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        How this is calculated
      </button>
      {open && (
        <div className="mt-3 text-xs space-y-1.5" data-testid="cash-how-calculated">
          <p><span className="briefing-hero__accent">Included:</span>{' '}
            {(e.accounts_included || []).map((a) => `${a.name} (${a.currency}, as of ${a.as_of || 'unknown'})`).join(', ') || '—'}</p>
          <p><span className="briefing-hero__accent">Accessible cash:</span> {e.accessible_rule}</p>
          <p><span className="briefing-hero__accent">Essential spending:</span>{' '}
            {plan.summary.safety_buffer.essential_monthly
              ? `${formatMoney(plan.summary.safety_buffer.essential_monthly, plan.base_currency)} a month, ${e.essential_window || 'recent months'}`
              : 'not established yet'}</p>
          <p><span className="briefing-hero__accent">Reserve target:</span>{' '}
            {plan.summary.safety_buffer.target_months}{' '}
            {plan.summary.safety_buffer.target_months === 1 ? 'month' : 'months'} of essential
            spending — your choice, not a rule. Three months is only the default; change it above and
            the buffer, the shortfall and the decisions all follow.</p>
          <p><span className="briefing-hero__accent">Excluded:</span> {(e.exclusions || []).filter(Boolean).join(', ')}</p>
          {e.conversion && (
            <p><span className="briefing-hero__accent">Converted:</span>{' '}
              reported in {e.conversion.reporting_currency} using {e.conversion.rate_source} rates from {e.conversion.rate_date}</p>
          )}
          {e.estimated && <p className="briefing-hero__muted">Some inputs are estimated, so treat the buffer as indicative.</p>}
        </div>
      )}
    </div>
  );
}

export function CashPlanHero() {
  const { plan, busy, error, refreshing, retry, setPlan } = useCashPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    return (
      <div data-testid="cash-plan-section">
        <div className="mb-4 mt-16 sm:mt-20">
          <h2 className="font-display text-2xl text-charcoal leading-tight">Your cash plan</h2>
          <p className="text-sm text-muted-foreground mt-0.5">What your money is for, and whether the near term is protected.</p>
        </div>
        {error
          ? <SectionError testid="cash-plan-hero-error" onRetry={retry}
                          title="We couldn't build your cash plan just now." />
          : <HeroSkeleton phase={phase} testid="cash-plan-hero-loading"
                          label="Working out your cash plan"
                          message="Calculating your safety buffer&hellip;" />}
      </div>
    );
  }
  if (plan.state === 'no_cash') return null;
  const ccy = plan.base_currency;
  const b = plan.summary.safety_buffer;
  const breakdown = plan.allocation_breakdown;
  const pct = b.state === 'ok' && b.target_months
    ? Math.min((b.months / b.target_months) * 100, 100) : 0;

  const headline = b.state === 'ok'
    ? `You have ${b.months} months of essential spending protected.`
    : (plan.state === 'needs_setup'
      ? 'Your cash has no job yet.'
      : 'We can\u2019t measure your safety buffer yet.');
  const support = b.state === 'ok'
    ? (b.shortfall_display > 0
      ? `Another ${formatMoney(b.shortfall_display, ccy)} would complete your ${b.target_months}-month reserve.`
      : `Your ${b.target_months}-month reserve is complete.`)
    : (b.unavailable_reason === 'no_safety_allocation_yet'
      ? 'Assign part of your cash to a safety reserve and this becomes a real number.'
      : 'Confirm your essential monthly spending and this becomes a real number.');

  return (
    <div data-testid="cash-plan-section">
      <div className="mb-4 mt-16 sm:mt-20">
        <h2 className="font-display text-2xl text-charcoal leading-tight">
          Your cash plan <UpdatingChip show={refreshing} testid="cash-plan-updating" />
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">What your money is for, and whether the near term is protected.</p>
      </div>

      <div className="briefing-hero p-5 sm:p-6 mb-4" data-testid="cash-plan-hero">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(260px,38%)]">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium">Your cash plan</p>
            <p className="font-display font-medium leading-tight mt-2" style={{ fontSize: 'clamp(22px, 2.2vw, 30px)' }}
               data-testid="cash-plan-headline">{headline}</p>
            <p className="text-sm briefing-hero__muted mt-1.5" data-testid="cash-plan-support">{support}</p>
            {b.state !== 'ok' && (
              <div className="mt-3" data-testid="cash-buffer-target-fallback">
                <BufferTargetControl months={b.target_months} onSaved={setPlan} tone="hero" />
              </div>
            )}
            {b.state === 'ok' && (
              <div className="mt-4 max-w-sm" data-testid="cash-buffer-progress">
                <div className="briefing-hero__progress-track h-2 rounded-full overflow-hidden">
                  <div className="briefing-hero__progress-fill h-full rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-1.5">
                  <p className="text-xs briefing-hero__muted">
                    {b.months} of {b.target_months} months · {formatMoney(b.essential_monthly, ccy)} essential spending a month
                  </p>
                  <BufferTargetControl months={b.target_months} onSaved={setPlan} tone="hero" />
                </div>
              </div>
            )}
            <HowCalculated plan={plan} />
          </div>

          <div className="briefing-hero__panel p-4 min-w-0" data-testid="cash-purpose-panel">
            <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium mb-2">What your cash is for</p>
            {breakdown.reconciles ? (
              <ul className="space-y-2">
                {PURPOSE_ROWS.map(({ key, label, Icon }) => (
                  <li key={key} className="flex items-baseline justify-between gap-3 text-xs"
                      data-testid={`cash-purpose-${key}`}>
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <Icon className="h-3.5 w-3.5 shrink-0 briefing-hero__accent" aria-hidden="true" />
                      {label}
                    </span>
                    <span className="tabular-nums shrink-0">
                      {money(breakdown.by_purpose[key].display, ccy) ?? '—'}
                    </span>
                  </li>
                ))}
                <li className="flex items-baseline justify-between gap-3 text-xs pt-2 mt-1 border-t border-white/10 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">{money(breakdown.total.display, ccy) ?? '—'}</span>
                </li>
              </ul>
            ) : (
              <p className="text-xs briefing-hero__muted">
                Your assigned amounts don&apos;t add up to your cash total yet, so this breakdown is
                being withheld until it does.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function UnassignedCashCard() {
  const { plan } = useCashPlan();
  if (!plan) return null;
  const ccy = plan.base_currency;
  const unassigned = plan.allocation_breakdown?.by_purpose?.not_assigned?.display || 0;
  if (unassigned <= 0) return null;
  const b = plan.summary.safety_buffer;
  const shortfall = b.state === 'ok' ? (b.shortfall_display || 0) : 0;

  return (
    <div className="bg-sage-light/40 rounded-2xl border border-sage/40 p-5 shadow-sm mb-4"
         data-testid="unassigned-cash-card">
      <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-1">
        Give unassigned cash a job
      </p>
      <p className="text-sm text-charcoal">
        {formatMoney(unassigned, ccy)} isn&apos;t assigned to bills, safety or a goal.
      </p>
      <ul className="text-xs text-muted-foreground mt-2 space-y-1">
        {shortfall > 0 && (
          <li data-testid="unassigned-option-buffer">
            Assigning {formatMoney(Math.min(shortfall, unassigned), ccy)} would
            {shortfall <= unassigned ? ' complete ' : ' move you toward '}
            your {b.target_months}-month safety buffer
            {shortfall <= unassigned
              ? `, leaving ${formatMoney(unassigned - shortfall, ccy)} flexible.`
              : '.'}
          </li>
        )}
        {(plan.goals || []).slice(0, 1).map((g) => (
          <li key={g.goal_id}>Or add to {g.title}, currently {g.percent_funded ?? 0}% funded.</li>
        ))}
        <li>Or keep it flexible — nothing moves until you say so.</li>
      </ul>
      <p className="text-[11px] text-muted-foreground mt-3" data-testid="unassigned-card-note">
        Assigning a purpose is planning only: no money moves and no balance changes.
        Use &ldquo;Give your money a job&rdquo; below to split an account and preview the effect first.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Decisions worth making — at most TWO, ordered by what actually matters now.
 * ------------------------------------------------------------------------- */
export function Protect30Card() {
  const { plan } = useCashPlan();
  const [open, setOpen] = useState(false);
  const p = plan?.next_30_days;
  if (!p || p.state === 'no_data') return null;
  const ccy = plan.base_currency;
  const known = p.known.display_amount;
  const est = p.expected_essentials.display_amount;
  const total = p.total_needed.display_amount;
  const gap = p.gap;
  const short = gap?.direction === 'short';

  return (
    <div className={`rounded-2xl border p-5 shadow-sm mb-4 ${short ? 'border-gold/40 bg-gold/5' : 'bg-sage-light/40 border-sage/40'}`}
         data-testid="protect-30-days-card">
      <div className="flex items-start gap-2">
        <CalendarClock className="h-4 w-4 mt-1 text-charcoal/70 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-1">
            Protect the next 30 days
          </p>
          {p.state === 'ok' ? (
            <p className="text-sm text-charcoal" data-testid="protect-30-headline">
              {short
                ? `You are about ${formatMoney(gap.amount, ccy)} short of what the next 30 days needs.`
                : `Your next 30 days looks covered, with about ${formatMoney(gap?.amount ?? 0, ccy)} to spare.`}
            </p>
          ) : (
            <p className="text-sm text-charcoal" data-testid="protect-30-incomplete">
              We can&apos;t give you a precise 30-day figure yet.
            </p>
          )}

          <ul className="text-xs text-charcoal/80 mt-2.5 space-y-1.5">
            <li className="flex items-baseline justify-between gap-3" data-testid="protect-30-known">
              <span>Known bills and commitments</span>
              <span className="tabular-nums">{known == null ? '—' : formatMoney(known, ccy)}</span>
            </li>
            <li className="flex items-baseline justify-between gap-3" data-testid="protect-30-estimate">
              <span>Expected variable essentials</span>
              <span className="tabular-nums">{est == null ? 'not established yet' : `about ${formatMoney(est, ccy)}`}</span>
            </li>
            <li className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-charcoal/10 font-medium"
                data-testid="protect-30-total">
              <span>Needed for the next 30 days</span>
              <span className="tabular-nums">{total == null ? 'withheld' : `about ${formatMoney(total, ccy)}`}</span>
            </li>
            {gap && (
              <li className="flex items-baseline justify-between gap-3" data-testid="protect-30-available">
                <span>Cash available for it — {gap.available_source}</span>
                <span className="tabular-nums">{formatMoney(gap.available_display, ccy)}</span>
              </li>
            )}
          </ul>

          {p.state !== 'ok' && (
            <ul className="text-xs text-muted-foreground mt-2.5 space-y-1.5" data-testid="protect-30-reasons">
              {p.incomplete_reasons.map((r) => (
                <li key={r.code}>
                  {r.message}
                  {r.code === 'stale_balances' && (
                    <span className="block mt-1.5">
                      <UpdateBalanceNudge tone="warning" testid="protect-30-update-nudge" />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <button type="button" onClick={() => setOpen((v) => !v)}
                  className="text-xs text-charcoal/70 underline underline-offset-2 mt-3 inline-flex items-center gap-1"
                  data-testid="protect-30-toggle">
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Where every figure comes from
          </button>
          {open && (
            <div className="mt-2.5 space-y-2 text-xs text-charcoal/80" data-testid="protect-30-detail">
              <div>
                <p className="font-medium">Known dated commitments</p>
                {p.known.items.length === 0 ? <p className="text-muted-foreground">None dated in this window.</p> : (
                  <ul className="mt-1 space-y-0.5">
                    {p.known.items.map((i, n) => (
                      <li key={`${i.name}-${n}`} className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 truncate">
                          {i.name} · due {i.due_date}{i.overdue ? ' · overdue' : ''} · {i.source_label}
                        </span>
                        <span className="tabular-nums shrink-0">
                          {formatMoney(i.amount, i.currency)}
                          {i.currency !== ccy && i.display_amount != null ? ` (${formatMoney(i.display_amount, ccy)})` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="font-medium">Expected variable essentials — {p.expected_essentials.source_label}</p>
                <p className="text-muted-foreground">
                  Median of {p.expected_essentials.months.map((m) => `${m.label}: ${formatMoney(m.display_amount, ccy)}`).join(' · ')}.
                  {p.expected_essentials.excluded_categories.length > 0 && (
                    ` Already counted as a known commitment, so excluded here: ${p.expected_essentials.excluded_categories.join(', ')}.`
                  )}
                </p>
              </div>
              {p.flexible.items.length > 0 && (
                <div data-testid="protect-30-flexible">
                  <p className="font-medium">
                    Flexible commitments — {p.flexible.display_amount == null ? '—' : formatMoney(p.flexible.display_amount, ccy)}
                  </p>
                  <p className="text-muted-foreground">
                    {p.flexible.note} {p.flexible.items.map((i) => `${i.name} (${formatMoney(i.amount, i.currency)}, due ${i.due_date})`).join(' · ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DecisionsSection() {
  const { plan, busy, error } = useCashPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    if (error) return null;              // the hero already owns the retry
    return (
      <div data-testid="cash-decisions-section">
        <SectionHeading title="Decisions worth making"
                        subtitle="Two at most, and only while they're still worth deciding."
                        testid="cash-decisions-heading" />
        <CardSkeleton phase={phase} testid="cash-decisions-loading" rows={4}
                      label="Working out what's worth deciding"
                      message="Checking your commitments for the next 30 days&hellip;" />
      </div>
    );
  }
  if (plan.state === 'no_cash') return null;
  const unassigned = plan.allocation_breakdown?.by_purpose?.not_assigned?.display || 0;
  const cards = [];
  if (plan.next_30_days && plan.next_30_days.state !== 'no_data') cards.push('protect');
  if (unassigned > 0) cards.push('unassigned');
  if (cards.length === 0) return null;

  return (
    <div data-testid="cash-decisions-section">
      <SectionHeading
        title="Decisions worth making"
        subtitle="Two at most, and only while they're still worth deciding."
        testid="cash-decisions-heading"
      />
      {cards.map((c, i) => (
        <div key={c} data-testid={`decision-card-${i + 1}`}>
          {c === 'protect' ? <Protect30Card /> : <UnassignedCashCard />}
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Give money jobs
 * ------------------------------------------------------------------------- */
export function MoneyJobsSection() {
  const { plan, busy, error, setPlan } = useCashPlan();
  const [editing, setEditing] = useState(null);
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    if (error) return null;
    return (
      <div data-testid="money-jobs-section">
        <SectionHeading title="Give your money a job"
                        subtitle="One account can do several jobs. Split it, preview the effect, then save — nothing moves."
                        testid="money-jobs-heading" />
        <ListSkeleton phase={phase} testid="money-jobs-loading" rows={2}
                      label="Loading what each account is for" />
      </div>
    );
  }
  if (!plan.accounts?.length) return null;

  return (
    <div data-testid="money-jobs-section">
      <SectionHeading
        title="Give your money a job"
        subtitle="One account can do several jobs. Split it, preview the effect, then save — nothing moves."
        testid="money-jobs-heading"
      />
      <div className="bg-card rounded-2xl border border-border shadow-sm divide-y divide-border overflow-hidden">
        {plan.accounts.map((a) => (
          <div key={a.id} className="p-4 flex flex-wrap items-center gap-3 justify-between"
               data-testid={`money-jobs-row-${a.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-charcoal truncate">{a.name}</p>
              <p className="text-xs text-muted-foreground">
                {formatMoney(a.balance, a.currency)}
                {a.as_of ? ` · reported ${a.as_of}` : ''}
                {a.currency !== plan.base_currency && a.display_balance != null
                  ? ` · ${formatMoney(a.display_balance, plan.base_currency)} converted`
                  : ''}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {a.purposes.length === 0
                  ? 'No job yet'
                  : a.purposes.map((p) => `${p.label}${p.goal_name ? ` → ${p.goal_name}` : ''}: ${formatMoney(p.amount, p.currency)}`).join(' · ')}
                {a.unassigned_amount > 0 && (
                  <span> · {formatMoney(a.unassigned_amount, a.currency)} unassigned</span>
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(a)}
                    data-testid={`money-jobs-cta-${a.id}`}>
              {a.purposes.length ? 'Change jobs' : 'Give it a job'}
            </Button>
          </div>
        ))}
      </div>
      {editing && (
        <AssignJobsDialog
          key={editing.id}
          account={editing}
          plan={plan}
          open
          onOpenChange={(v) => { if (!v) setEditing(null); }}
          onSaved={(next) => setPlan(next)}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Savings connected to your life · Savings momentum
 * ------------------------------------------------------------------------- */
export function ConnectedSavingsSection() {
  const { plan, busy, error } = useCashPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    if (error) return null;
    return (
      <div data-testid="connected-savings-section">
        <SectionHeading title="Savings connected to your life"
                        subtitle="Cash you've assigned to a goal, and whether your cash is actually growing."
                        testid="connected-savings-heading" />
        <div className="grid gap-4 lg:grid-cols-2">
          <CardSkeleton phase={phase} testid="cash-goals-loading" rows={3}
                        label="Loading goals funded by this cash" />
          <CardSkeleton phase={phase} testid="momentum-loading" rows={3}
                        label="Working out your savings momentum"
                        message="Comparing six months of cash movement&hellip;" />
        </div>
      </div>
    );
  }
  const goals = plan.goals || [];
  const m = plan.momentum;
  if (!goals.length && !m) return null;

  return (
    <div data-testid="connected-savings-section">
      <SectionHeading
        title="Savings connected to your life"
        subtitle="Cash you've assigned to a goal, and whether your cash is actually growing."
        testid="connected-savings-heading"
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-card rounded-2xl border border-border shadow-sm p-5" data-testid="cash-goals-card">
          <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-2">Goals funded by this cash</p>
          {goals.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="cash-goals-empty">
              No cash is assigned to a goal yet. Assigning some doesn&apos;t move it — it just
              stops it being counted twice.
            </p>
          ) : (
            <ul className="space-y-3">
              {goals.map((g) => (
                <li key={g.goal_id} data-testid={`cash-goal-${g.goal_id}`}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate text-charcoal">{g.title}</span>
                    <span className="tabular-nums shrink-0">
                      {g.assigned_display == null ? '—' : formatMoney(g.assigned_display, plan.base_currency)}
                      {g.target_amount ? ` of ${formatMoney(g.target_amount, g.currency)}` : ''}
                    </span>
                  </div>
                  {g.percent_funded != null && (
                    <div className="h-1.5 rounded-full bg-muted mt-1.5 overflow-hidden">
                      <div className="h-full rounded-full bg-sage" style={{ width: `${Math.min(g.percent_funded, 100)}%` }} />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {g.percent_funded != null ? `${g.percent_funded}% funded from cash` : 'No target set'}
                    {g.linked_accounts?.length ? ` · from ${g.linked_accounts.join(', ')}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <MomentumCard />
      </div>
    </div>
  );
}

export function MomentumCard() {
  const { plan } = useCashPlan();
  const m = plan?.momentum;
  if (!m) return null;
  const ccy = m.currency;
  const covered = m.months.filter((x) => x.covered && x.net_display != null);
  const peak = Math.max(1, ...covered.map((x) => Math.abs(x.net_display)));
  const Icon = m.direction === 'growing' ? TrendingUp : (m.direction === 'shrinking' ? TrendingDown : Minus);

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-5" data-testid="momentum-card">
      <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-1">Savings momentum</p>
      {m.state === 'ok' ? (
        <>
          <p className="text-sm text-charcoal inline-flex items-center gap-1.5" data-testid="momentum-headline">
            <Icon className="h-4 w-4 text-sage" aria-hidden="true" />
            {m.direction === 'steady'
              ? 'Your cash is holding roughly steady month to month.'
              : `Your cash is ${m.direction} by about ${formatMoney(Math.abs(m.average_monthly), ccy)} a month.`}
          </p>
          <div className="flex items-end gap-2 mt-4 h-24" data-testid="momentum-chart">
            {m.months.map((x) => (
              <div key={x.label} className="flex-1 flex flex-col items-center justify-end gap-1">
                <div className={`w-full rounded-t ${x.net_display >= 0 ? 'bg-sage' : 'bg-gold'}`}
                     style={{ height: x.net_display == null ? 2 : `${Math.max(3, (Math.abs(x.net_display) / peak) * 100)}%` }}
                     title={x.net_display == null ? 'no data' : formatMoney(x.net_display, ccy)} />
                <span className="text-[10px] text-muted-foreground">{x.label.split(' ')[0]}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {m.source_label}. Transfers between your own accounts are excluded — moving money
            isn&apos;t saving it.
          </p>
        </>
      ) : (
        <div data-testid="momentum-incomplete">
          <p className="text-sm text-charcoal">We can&apos;t show a trend yet.</p>
          <ul className="text-xs text-muted-foreground mt-1.5 space-y-1">
            {m.reasons.map((r) => <li key={r.code}>{r.message}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
